/**
 * Componente Spinner
 *
 * Indicador de carregamento animado com diferentes tamanhos.
 *
 * @author Equipe VendeAi
 * @since 1.0.0
 */

import { clsx } from 'clsx';

/**
 * Props do componente Spinner
 */
interface SpinnerProps {
  /** Tamanho do spinner */
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /** Cor do spinner */
  color?: 'primary' | 'secondary' | 'white' | 'current';
  /** Classes CSS adicionais */
  className?: string;
}

/**
 * Classes de tamanho
 */
const sizeClasses = {
  sm: 'h-4 w-4 border-2',
  md: 'h-6 w-6 border-2',
  lg: 'h-8 w-8 border-3',
  xl: 'h-12 w-12 border-4',
};

/**
 * Classes de cor
 */
const colorClasses = {
  primary: 'border-primary-800 border-t-transparent dark:border-primary-400',
  secondary: 'border-secondary-700 border-t-transparent',
  white: 'border-white border-t-transparent',
  current: 'border-current border-t-transparent',
};

/**
 * Componente Spinner
 *
 * Exibe um indicador de carregamento circular animado.
 *
 * @example
 * <Spinner size="lg" color="primary" />
 *
 * @example
 * // Loading de página inteira
 * <div className="flex h-screen items-center justify-center">
 *   <Spinner size="xl" />
 * </div>
 */
export function Spinner({
  size = 'md',
  color = 'primary',
  className,
}: SpinnerProps) {
  return (
    <div
      role="status"
      aria-label="Carregando"
      className={clsx(
        'animate-spin rounded-full',
        sizeClasses[size],
        colorClasses[color],
        className,
      )}
    >
      {/* Screen reader text */}
      <span className="sr-only">Carregando...</span>
    </div>
  );
}
